import { AppForm } from '../../../../../../../common/forms/app.form';
import { FormControl, Validators } from '@angular/forms';
import { CustomValidator, fromToDate, oneRequired } from '../../../../../../../common/forms/custom.validators';
import { calcDaysBetweenDates } from '../../../../../../../services/helpers';
import { ParcelAppEntityParcelWrite, RoadOccupationStageAppEntityApplicationRead } from '../../../../../../../openapi';

export function toStageFormValue(v: RoadOccupationStageAppEntityApplicationRead): LocationOfDevicesUnrelatedToRoadOccupationStageFormValue {
  return {
    startDate: v?.startDate || null,
    endDate: v?.endDate || null,
    dayQuantity: v?.dayQuantity || 0,

    devicesProvidePlacementDeadline: !!v?.devicesProvidePlacementDeadline,

    nextYearsDateFrom: v?.nextYearsDateFrom,
    nextYearsDateTo: v?.nextYearsDateTo,
    yearsCount: v?.yearsCount,

    provideRoadNumberAndName: !!v?.provideRoadNumberAndName,
    city: v?.city || '',

    roadNumber: v?.roadNumber || '',
    roadName: v?.roadName || '',
    street: v?.street || '',

    parcels: v?.parcels || [],

    devicesCountyRoadsArea: (v?.devicesCountyRoadsArea || 0).toFixed(2).replace('.', ','),
    devicesOnBridgeArea: (v?.devicesOnBridgeArea || 0).toFixed(2).replace('.', ','),
    infrastructureEquipmentArea: (v?.infrastructureEquipmentArea || 0).toFixed(2).replace('.', ',')
  };
}

export interface LocationOfDevicesUnrelatedToRoadOccupationStageFormValue {
  startDate: Date | null;
  endDate: Date | null;
  dayQuantity: number;

  devicesProvidePlacementDeadline: boolean;

  nextYearsDateFrom?: Date | null;
  nextYearsDateTo?: Date | null;
  yearsCount?: number;

  provideRoadNumberAndName: boolean;
  city: string;

  roadName?: string;
  roadNumber?: string;
  street?: string;

  parcels: ParcelAppEntityParcelWrite[];

  devicesCountyRoadsArea: string;
  devicesOnBridgeArea: string;
  infrastructureEquipmentArea: string;
}

export class LocationOfDevicesUnrelatedToRoadOccupationStageForm extends AppForm {
  constructor() {
    super({
      startDate: new FormControl(null, { validators: [Validators.required, CustomValidator.isValidDateFormat(), CustomValidator.minDate(new Date())] }),
      endDate: new FormControl(null, { validators: [Validators.required, CustomValidator.isValidDateFormat()] }),
      dayQuantity: new FormControl(null, [Validators.required, Validators.maxLength(255)]),

      devicesProvidePlacementDeadline: new FormControl(false, { nonNullable: true }),

      provideRoadNumberAndName: new FormControl(true, { nonNullable: true }),

      parcels: new FormControl([], {
        nonNullable: true,
        validators: [
          Validators.minLength(1),
          Validators.required
        ]
      }),

      city: new FormControl(
        '',
        { nonNullable: true, validators: [Validators.maxLength(255), Validators.required] }
      ),

      devicesCountyRoadsArea: new FormControl(
        '',
        {
          validators: [Validators.min(0), Validators.maxLength(255)],
          nonNullable: true
        }
      ),
      devicesOnBridgeArea: new FormControl(
        '',
        {
          validators: [Validators.min(0), Validators.maxLength(255)],
          nonNullable: true
        }
      ),

      infrastructureEquipmentArea: new FormControl(
        '',
        {
          validators: [Validators.min(0), Validators.maxLength(255)],
          nonNullable: true
        }
      )
    }, {
      validators: [
        oneRequired(['devicesCountyRoadsArea', 'devicesOnBridgeArea', 'infrastructureEquipmentArea'], true),
        fromToDate('startDate', 'endDate'),
        fromToDate('nextYearsDateFrom', 'nextYearsDateTo')
      ]
    });
    this.provideRoadNumberAndNameChange(true);
    this.devicesProvidePlacementDeadlineChange(false);
  }

  public get devicesProvidePlacementDeadline(): FormControl {
    return this.get('devicesProvidePlacementDeadline') as FormControl;
  }

  public devicesProvidePlacementDeadlineChange(v: boolean): void {
    if (v) {
      const d = new Date();
      d.setFullYear(d.getFullYear() + 1, 0, 1);
      d.setHours(0, 0, 0, 0);

      this.addControl('nextYearsDateFrom', new FormControl(d, { validators: [Validators.required, CustomValidator.isValidDateFormat(), CustomValidator.minDate(d)] }));

      this.addControl('nextYearsDateTo', new FormControl(d, { validators: [Validators.required, CustomValidator.isValidDateFormat()] }));

      this.addControl('yearsCount', new FormControl(null));
    } else {
      this.removeControl('nextYearsDateFrom');
      this.removeControl('nextYearsDateTo');
      this.removeControl('yearsCount');
    }
  }

  public calcYearsCount(): void {
    setTimeout(() => {
      const nextYearsDateFrom = this.nextYearsDateFrom?.value;
      const nextYearsDateTo = this.nextYearsDateTo?.value;
      if (!nextYearsDateFrom || !nextYearsDateTo) {
        this.yearsCount?.setValue('');
      } else {
        const d1 = new Date(nextYearsDateFrom);
        const d2 = new Date(nextYearsDateTo);
        this.yearsCount?.setValue(d2.getFullYear() - d1.getFullYear() + 1);
      }
    });
  }

  public get nextYearsDateFrom(): FormControl | null {
    return this.get('nextYearsDateFrom') as FormControl | null;
  }

  public get nextYearsDateTo(): FormControl | null {
    return this.get('nextYearsDateTo') as FormControl | null;
  }

  public get yearsCount(): FormControl | null {
    return this.get('yearsCount') as FormControl | null;
  }

  public get parcels(): FormControl {
    return this.get('parcels') as FormControl;
  }

  public get provideRoadNumberAndName(): FormControl {
    return this.get('provideRoadNumberAndName') as FormControl;
  }

  public get city(): FormControl {
    return this.get('city') as FormControl;
  }

  public cityChange(v?: string | null): void {
    this.street?.setValue('');
    this.street?.markAsUntouched();
    this.roadNumber?.setValue('');
    this.roadNumber?.markAsUntouched();
  }

  public provideRoadNumberAndNameChange(v?: boolean): void {
    this.city.setValue('');
    this.city.markAsUntouched();
    if (v) {
      this.addControl('roadNumber', new FormControl('', {
        validators: [Validators.required, Validators.maxLength(255)],
        nonNullable: true
      }));

      this.addControl('roadName', new FormControl({
        disabled: true,
        value: null
      }, [Validators.required, Validators.maxLength(255)]));

      this.removeControl('street');
    } else {
      this.removeControl('roadName');
      this.removeControl('roadNumber');
      this.addControl('street', new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(255)]
      }));
    }
  }

  public get roadName(): FormControl | null {
    return this.get('roadName') as FormControl | null;
  }

  public get roadNumber(): FormControl | null {
    return this.get('roadNumber') as FormControl | null;
  }

  public get street(): FormControl | null {
    return this.get('street') as FormControl | null;
  }

  public get startDate(): FormControl {
    return this.get('startDate') as FormControl;
  }

  public get endDate(): FormControl {
    return this.get('endDate') as FormControl;
  }

  public get dayQuantity(): FormControl {
    return this.get('dayQuantity') as FormControl;
  }

  public startDateChange(v: Date): void {
    if (v && this.endDate.value) {
      this.dayQuantity.setValue(
        calcDaysBetweenDates(new Date(v), new Date(this.endDate.value))
      );
    } else {
      this.dayQuantity.setValue('');
    }
  }

  public endDateChange(v: Date): void {
    if (v && this.startDate.value) {
      this.dayQuantity.setValue(
        calcDaysBetweenDates(new Date(this.startDate.value), new Date(v))
      );
    } else {
      this.dayQuantity.setValue('');
    }
  }

  public override patchFormValues(value: LocationOfDevicesUnrelatedToRoadOccupationStageFormValue) {
    this.provideRoadNumberAndNameChange(value.provideRoadNumberAndName);
    this.devicesProvidePlacementDeadlineChange(value.devicesProvidePlacementDeadline);
    super.patchFormValues({
      ...value,
      provideRoadNumberAndName: value.provideRoadNumberAndName,
      devicesProvidePlacementDeadline: value.devicesProvidePlacementDeadline
    });
  }

  public get values(): LocationOfDevicesUnrelatedToRoadOccupationStageFormValue {
    return {
      startDate: this.value.startDate,
      endDate: this.value.endDate,
      dayQuantity: this.dayQuantity.value,

      devicesProvidePlacementDeadline: this.value.devicesProvidePlacementDeadline,

      nextYearsDateFrom: this.nextYearsDateFrom?.value,
      nextYearsDateTo: this.nextYearsDateTo?.value,
      yearsCount: this.yearsCount?.value,

      provideRoadNumberAndName: this.value?.provideRoadNumberAndName,
      city: this.value.city,

      roadName: this.roadName?.value || null,
      roadNumber: this.value?.roadNumber || null,
      street: this.street?.value || null,

      parcels: this.parcels.value,

      devicesCountyRoadsArea: this.value.devicesCountyRoadsArea,
      devicesOnBridgeArea: this.value.devicesOnBridgeArea,
      infrastructureEquipmentArea: this.value.infrastructureEquipmentArea
    };
  }
}
